import React from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Chip, Stack, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { Handle, NodeProps, Position } from 'reactflow';
import { TaskEditorValue } from '@features/Admin/Agent/taskFlow/domain/contracts';
import { TASK_TYPE_REGISTRY } from '@features/Admin/Agent/taskFlow/domain/taskTypeRegistry';

export type TaskFlowNodeData = {
  task: TaskEditorValue;
  index: number;
  hasError?: boolean;
};

export const TaskFlowNode: React.FC<NodeProps<TaskFlowNodeData>> = ({ data, selected }) => {
  const { t } = useTranslation();
  const meta = TASK_TYPE_REGISTRY[data.task.type];
  const hasError = Boolean(data.hasError);

  return (
    <Box
      sx={{
        width: 200,
        minHeight: 70,
        borderRadius: 3,
        border: (theme) =>
          hasError
            ? `1.5px solid ${theme.palette.error.main}`
            : `1px solid ${theme.palette.divider}`,
        backgroundColor: (theme) => theme.palette.background.paper,
        p: 1.25,
        boxShadow: (theme) =>
          selected
            ? `0 0 0 2px ${alpha(hasError ? theme.palette.error.main : theme.palette.primary.main, 0.45)}, ${theme.shadows[2]}`
            : theme.shadows[1],
        transition: 'border-color 140ms ease, box-shadow 140ms ease',
      }}>
      <Handle
        type="target"
        position={Position.Left}
        style={{ width: 10, height: 10, border: '2px solid #111827', background: meta.color }}
      />
      <Stack spacing={0.5}>
        <Stack direction="row" spacing={0.75} alignItems="center">
          <Box
            sx={{
              width: 10,
              height: 10,
              borderRadius: '50%',
              backgroundColor: meta.color,
              flexShrink: 0,
            }}
          />
          <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary' }}>
            {data.index + 1}. {t(`assistantsPage.modals.taskFlow.${meta.shortLabelKey}`)}
          </Typography>
        </Stack>
        <Typography variant="subtitle2" sx={{ fontWeight: 600, lineHeight: 1.25 }} noWrap>
          {data.task.id}
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.3 }} noWrap>
          {data.task.description || t('assistantsPage.modals.taskFlow.noDescription')}
        </Typography>
        {hasError && (
          <Chip
            size="small"
            label={t('assistantsPage.modals.taskFlow.needsAttention')}
            color="error"
            variant="outlined"
            sx={{ width: 'fit-content', height: 20, fontSize: 11 }}
          />
        )}
      </Stack>
      <Handle
        type="source"
        position={Position.Right}
        style={{ width: 10, height: 10, border: '2px solid #111827', background: meta.color }}
      />
    </Box>
  );
};
